/* tslint:disable max-line-length no-string-literal */
import { Component, Inject, Renderer2, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { ToolSettingsService } from 'src/app/service/tool-settings/tool-settings.service';
import { ToolsService } from 'src/app/service/tools/tools.service';
import { CurrencyPipe } from '@angular/common';
import { Location } from '@angular/common';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ToolFormComponent } from '../shared/tool-form/tool-form.component';
import { forkJoin } from 'rxjs';
import { TableData } from 'src/app/shared/models/tableData';
import { assignment } from '../tool-transfer/tool-transfer.functions';
import { MediaService } from 'src/app/service/media/media.service';
import { DialogService } from 'src/app/service/dialog/dialog.service';
import { ToolPermissionsService } from 'src/app/service/auth/tool-permissions.service';
import { PermissionsService } from 'src/app/service/auth/permissions.service';
import type { ToolSearchParams } from 'src/app/shared/models/toolSearchParams';

@Component({
  selector: 'app-model-tool-page',
  templateUrl: './tool-model-page.component.html',
  styleUrls: ['./tool-model-page.component.scss'],
})
export class ToolModelPageComponent extends ToolFormComponent implements OnInit {
  categories: Array<any> = [];
  manufacturers: Array<any> = [];
  assetClasses: Array<any> = [];

  toolsTableData: TableData = {
    label: 'Tool',
    type: 'tools',
    hideTableBar: true,
    hideTablePaging: true,
    selectable: false,
    statusToggle: false,
    columns: [
      {
        name: 'toolId',
        label: 'Tool ID',
        property: 'toolId',
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '104px' },
        columnClasses: { 'text-truncate': true, another: true },
        show: row => {
          const allowed = this.permissionsService.allow({ resource1: 'tools', action: 'detailed_view' });
          return allowed;
        },
        link: row => {
          return [`/tools/edit/${row.id}`];
        },
      },
      {
        name: 'description',
        label: 'Description',
        property: 'description',
        sortable: true,
        columnStyles: { 'max-width': '210px' },
        columnClasses: { 'text-truncate': true, another: true },
      },
      {
        name: 'serialNumber',
        label: 'Serial #',
        property: 'serialNumber',
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '104px' },
        columnClasses: { 'text-truncate': true, another: true },
      },
      {
        name: 'lastAssignedDate',
        label: 'Last Assigned',
        property: row => assignment(row, 'assignedDate'),
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '104px' },
        columnClasses: { 'text-truncate': true, another: true },
      },
      {
        name: 'assignedTo',
        label: 'Assigned To',
        property: row => assignment(row, 'primaryAssignment'),
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '104px' },
        columnClasses: { 'text-truncate': true, another: true },
      },
      {
        name: 'status',
        label: 'Status',
        property: row => assignment(row, 'type.name'),
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '104px' },
        columnClasses: { 'text-truncate': true, another: true },
        component: 'tool-status',
      },
    ],
    rowActions: [],
    data: [],
    pagination: {},
    meta: {},
  };

  constructor(
    /* tslint:disable-next-line variable-name */
    @Inject(DOCUMENT) public _document,
    public renderer2: Renderer2,
    public currencyPipe: CurrencyPipe,
    public location: Location,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public toolsService: ToolsService,
    public toastService: ToastService,
    public toolSettingsService: ToolSettingsService,
    public mediaService: MediaService,
    public dialogService: DialogService,
    public permissionsService: PermissionsService,
    public toolPermissionsService: ToolPermissionsService,
  ) {
    super(
      'models',
      _document,
      renderer2,
      location,
      currencyPipe,
      activatedRoute,
      router,
      toolsService,
      toastService,
      mediaService,
      dialogService,
      toolPermissionsService,
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.fetch();
  }

  fetch() {
    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      const id = +params.get('id');
      this.toolsService.readById[this.dataType](id).subscribe(res => {
        this.fetchSuccess(res);
      });
    });
  }

  fetchSuccess(res: any) {
    this.populatePayloadModel(res);
    this.populateDataModel(res);
    this.fetchOthers();
  }

  fetchCategoriesByAssetClass() {
    const categoriesParams = {
      embed: 'assetClass',
      filter: `enabled=1,assetClassId=${this.dataModel.assetClassId}`,
      page: 1,
      limit: 1000,
    };
    this.toolSettingsService.read['categories-assetclass'](categoriesParams).subscribe(results => {
      this.categories = results.data;
    });
  }

  fetchOthers() {
    const params: ToolSearchParams = {}
    const isOnModelsPage = this.dataType === 'models'

    if (isOnModelsPage) {
      params.filter = 'enabled=1'
    }

    const requests = [
      this.toolSettingsService.read['categories'](params),
      this.toolSettingsService.read['asset-classes'](params),
      this.toolSettingsService.read['manufacturers'](params),
    ];

    forkJoin(requests).subscribe(
      res => {
        this.categories = res[0].data;
        this.assetClasses = res[1].data;
        this.manufacturers = res[2].data;
        this.loadSelect2();
        this.loadDatePicker();
      },
      error => {
        console.error(error);
      },
    );
  }

  async handleSave(redirectBack: boolean = true) {
    this.submitted = true;

    if (!this.toolModelForm.valid) return;
    if (!this.dataModel.id) return

    const payload = this.getPayload();

    await this.updateModel(payload, redirectBack)
      .then(() => {
        if (redirectBack) this.navigateBack();
      })
      .catch(err => {
        console.error(err)
      });
  }

  async updateModel(payload, redirectBack) {
    await this.updatePromise(payload, redirectBack)
      .catch(err => console.error(err));
  }

  handleAddTool() {
    if (!this.dataModel.id) return this.create()

    const payload = this.getPayload();
    this.updateModel(payload, false).then(() => {
      this.createTool();
    });
  }

  createTool() {
    this.toolsService.create['tools']().subscribe(
      res => {
        this.navigateToEditTool(res.data.id, this.dataModel);
      },
      err => {
        console.error('error:', err);
      },
    );
  }

  create() {
    this.toolsService.create[this.dataType](this.dataModel).subscribe(
      () => {
        this.navigateBack();
      },
      error => {
        console.error(error);
      },
    );
  }

  navigateToEditTool(toolId, payload) {
    const createToolPayload = {
      id: toolId,
      data: {
        toolModelId: payload.id,
        inspectionRequired: payload.inspectionRequired,
        maintenanceRequired: payload.maintenanceRequired,
      },
    };

    this.toolsService.update['tools'](createToolPayload).subscribe(res => {
      this.router.navigate(['/tools/add/', toolId], { relativeTo: this.activatedRoute });
    }),
      err => {
        console.error('error:', err);
      };
  }

  handleCancel(shouldStayOnPage?: boolean) {
    if (this.payloadModel.status !== 'Draft') {
      this.dialogService.close('delete-model-dialog')

      if (!shouldStayOnPage) this.navigateBack()
      return
    }

    const params = { draftCancel: true };
    this.toolsService.delete[this.dataType](this.dataModel.id, params).subscribe(() => {
      this.navigateBack();
    });
  }

  populatePayloadModel(res: any) {
    const { data } = res;
    this.data = data;
    this.payloadModel.name = data.name || data.placeholder || '';
    this.payloadModel.allowVehicleDetails = data.allowVehicleDetails || false;
    this.payloadModel.description = data.description || '';
    this.payloadModel.notes = data.notes || null;
    this.payloadModel.modelYear = data.modelYear || null;
    this.payloadModel.categoryId = data.categoryId || 0;
    this.payloadModel.manufacturerId = data.manufacturerId || 0;
    this.payloadModel.status = data.status || 'Draft';
    this.payloadModel.rentalPriceDaily = data.rentalPriceDaily || 0;
    this.payloadModel.rentalPriceWeekly = data.rentalPriceWeekly || 0;
    this.payloadModel.rentalPriceMonthly = data.rentalPriceMonthly || 0;
    this.payloadModel.maintenanceRequired = data.maintenanceRequired || false;
    this.payloadModel.inspectionRequired = data.inspectionRequired || false;
  }

  populateDataModel(res: any) {
    const { data, meta } = res;

    const possibleImages = this.filterAssetByCategory(data.images, 'image') || [];
    const possibleDocuments = this.filterAssetByCategory(data.documents, 'document') || [];

    this.dataModel.id = data.id;
    this.dataModel.thumbnail = data.thumbnail || null;
    this.dataModel.assetClassId = data.category?.assetClass?.id || null;
    this.dataModel.images = this.getNestedAsset(possibleImages)
    this.dataModel.documents = this.getNestedAsset(possibleDocuments)
    this.dataModel.tools = data.tools || [];
    this.dataModel.meta = meta || null;
    this.dataModel.maintenance = this.getNestedAsset(data.maintenance)
    this.dataModel.inspection = this.getNestedAsset(data.inspection)
    this.dataModel.status = data.status || null;
    this.dataModel.maintenanceRequired = data.maintenanceRequired || false;
    this.dataModel.inspectionRequired = data.inspectionRequired || false;
  }

  getNestedAsset(assetData: unknown[]) {
    const hasAssets = Array.isArray(assetData) && assetData?.length

    if (!hasAssets) return []

    return assetData.map(el => el.media)
  }

  filterAssetByCategory(payload: Record<string, unknown>[], category: string): Array<unknown> {
    if (!Array.isArray(payload)) return []
    if (!payload.length) return []

    const hasNestedMedia = payload.some(el => el.media)

    if (hasNestedMedia) return payload

    return payload.filter(x => x.category === category);
  }

  getPayload() {
    this.payloadModel.status = 'Published';
    this.cleanPayloadModel(['rentalPriceDaily', 'rentalPriceWeekly', 'rentalPriceMonthly']);
    return super.getPayload();
  }

  updatePayload(field, data) {
    this.setPayload(field, data.required);
  }

  hasImage() {
    const isArray = Array.isArray(this.dataModel?.thumbnail) && this.dataModel?.thumbnail?.length
    return isArray || !!this.dataModel?.thumbnail?.url
  }

  getImageLink() {
    const hasMultipleImages = Array.isArray(this.dataModel?.thumbnail) && this.dataModel?.thumbnail?.length

    if (hasMultipleImages) {
      return this.dataModel?.thumbnail[0].url
    }

    return this.dataModel?.thumbnail?.url ?? '/assets/images/image-not-found.jpg'
  }
}
